import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, MessageSquare, CheckCircle, Zap } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const HomePage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActiveEvents();
  }, []);

  const fetchActiveEvents = async () => {
    try {
      const response = await api.get('/simple.php?action=public-events');
      if (response.data.success && response.data.data.length > 0) {
        setEvents(response.data.data);
      } else {
        // No events found - try to initialize demo data
        console.log('No events found, initializing demo data...');
        try {
          await api.get('/simple.php?action=init-data');
          // Try fetching events again after init
          const retryResponse = await api.get('/simple.php?action=public-events');
          if (retryResponse.data.success) {
            setEvents(retryResponse.data.data);
          }
        } catch (initError) {
          console.error('Failed to initialize demo data:', initError);
          toast.error('No events available. Please contact administrator.');
        }
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load active events');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="loading">
        Loading active events...
      </div>
    );
  }

  return (
    <div className="container" style={{ 
      padding: '16px', 
      maxWidth: '100%',
      minHeight: '100vh'
    }}>
      {/* Compact Header */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '24px', 
        background: '#3b82f6', 
        color: 'white',
        padding: '20px 16px',
        borderRadius: '8px',
        margin: '0 -8px 24px -8px'
      }}>
        <h1 style={{ 
          fontSize: '1.5rem', 
          marginBottom: '8px', 
          fontWeight: '600',
          margin: 0
        }}>
          QAME System
        </h1>
        <p style={{ 
          fontSize: '0.9rem', 
          opacity: 0.9, 
          margin: 0,
          fontWeight: '400'
        }}>
          Event Evaluation Platform
        </p>
      </div>

      {/* Available Events */}
      <div>
        <h2 style={{ 
          fontSize: '1.2rem',
          marginBottom: '16px', 
          color: '#1f2937',
          fontWeight: '600',
          textAlign: 'center'
        }}>
          Available Evaluations
        </h2>

        {events.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '32px 16px',
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }}>
            <Calendar size={32} style={{ color: '#9ca3af', marginBottom: '12px' }} />
            <p style={{ color: '#6b7280', margin: 0, fontSize: '0.9rem' }}>
              No active events available
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {events.map((event) => (
              <div key={event.events_id} style={{ 
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start', 
                  marginBottom: '8px' 
                }}>
                  <h3 style={{ 
                    color: '#1f2937', 
                    margin: 0, 
                    fontSize: '1rem',
                    fontWeight: '600',
                    flex: 1
                  }}>
                    {event.event_name}
                  </h3>
                  {event.is_active && (
                    <span style={{ 
                      background: '#10b981', 
                      color: 'white', 
                      padding: '2px 6px', 
                      borderRadius: '4px', 
                      fontSize: '10px',
                      fontWeight: '500'
                    }}>
                      Active
                    </span>
                  )}
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '6px', 
                    color: '#6b7280',
                    fontSize: '0.8rem'
                  }}>
                    <Calendar size={12} />
                    <span>
                      {formatDate(event.event_datefrom)} - {formatDate(event.event_dateto)}
                    </span>
                  </div>
                </div>

                <Link 
                  to={`/evaluate/${event.events_id}`}
                  style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%', 
                    textDecoration: 'none',
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '12px 16px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    gap: '6px'
                  }}
                >
                  Start Evaluation
                  <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Simple Footer */}
      <div style={{ 
        marginTop: '32px',
        textAlign: 'center',
        padding: '16px',
        color: '#6b7280',
        fontSize: '0.8rem'
      }}>
        <p style={{ margin: 0 }}>
          Your feedback helps us improve our services
        </p>
      </div>
    </div>
  );
};

export default HomePage;